import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NoSelfieIcon from "@mui/icons-material/DoDisturbAltOutlined";
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import PercentageChange from "../components/dashboard/PercentageChange";
import { IColumn } from "../components/table/data/DataTable";
import { eLanguages, flags, languages } from "../i18n";
import IContact from "../models/contact";
import { ELanguage } from "../models/enums/language";
import { ERole } from "../models/enums/role";
import { EUserNotificationCode } from "../models/enums/userNotificationCode";
import { EFormType, IForm } from "../models/form/form";
import IMiscleanedLocalsDisplay from "../models/globalDashboard/miscleanedLocalsDisplay";
import IEmployeeTimesheetOverview from "../models/globalDashboard/userTimesheetOverview";
import { ILocalDisplay } from "../models/requests/local/localDisplay";
import { IScan, IScanDisplayWithId } from "../models/stat";
import { ITicketDisplay } from "../models/ticket/ticketDisplay";
import IUserNotificationDetail from "../models/userNotificationDetail";
import { IUserDetails } from "../pages/table/user/UserPage";
import auth from "../services/auth/AuthService";

const NO_DATA = "- - -";
const TEXT_MAX_DISPLAYED_LENGTH = 20;

export const useLocalColumns = () => {
    const { t } = useTranslation();

    const columns: IColumn<ILocalDisplay>[] = [
        {
            field: "name",
            headerName: t("local.name"),
        },
        {
            field: "profileName",
            headerName: t("local.profile"),

        },
        {
            field: "floorName",
            headerName: t("table.local.floor"),
        },
        {
            field: "buildingName",
            headerName: t("table.local.building"),
        },
        {
            field: "tagMacAddress",
            headerName: t("table.local.tagMacAddress"),
            transform: {
                render: (data) => (
                    <>
                        {data?.tagMacAddress || "X"}
                    </>
                ),
                filterTransform: (data) => {
                    return data?.tagMacAddress?.toString() || "X"
                }
            }
        },
        {
            field: "servicePlusStickerCode",
            headerName: t("table.local.servicePlusStickerCode"),
            transform: {
                render: (data) => (
                    <>
                        {data?.servicePlusStickerCode || "X"}
                    </>
                ),
                filterTransform: (data) => {
                    return data?.servicePlusStickerCode?.toString() || "X"
                }
            }
        },
        {
            field: "codes",
            headerName: t("local.codes"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.codes.join(", ") || "X"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.codes.join(", ") || "X";
                },
            },
        },
        {
            field: "routes",
            headerName: t("table.route.name"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.routes && data.routes.map(r => r.name).join(", ") || "X"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.routes && data.routes.map(r => r.name).join(", ") || "X";
                },
            },
        },
        {
            field: "isLocked",
            headerName: t("table.local.status"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.isLocked ? t("table.local.inactive") : t("table.local.active")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.isLocked ? t("table.local.inactive") : t("table.local.active");
                },
            },
        },
    ];
    return columns;
};

export const useUserColumns = () => {
    const { t } = useTranslation();

    const columns: IColumn<IUserDetails>[] = [
        {
            field: "code",
            headerName: "Code",
        },
        {
            field: "firstName",
            headerName: t("table.user.firstName"),
        },
        {
            field: "lastName",
            headerName: t("table.user.lastName"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.lastName ?? NO_DATA}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.lastName ?? NO_DATA;
                },
            },
        },
        {
            field: "email",
            headerName: t("table.user.email"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.email ?? NO_DATA}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.email ?? NO_DATA;
                },
            },
        },
        {
            field: "role",
            headerName: t("table.user.role"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {t(`table.user.${ERole[data.role] || NO_DATA}`)}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return t(`table.user.${ERole[data.role] || NO_DATA}`);
                },
            },
        },
        {
            field: "isLocked",
            headerName: t("table.user.status"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.isLocked ? t("table.user.inactive") : t("table.user.active")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.isLocked ? t("table.user.inactive") : t("table.user.active");
                },
            },
        },
        {
            field: "lastSessionDate",
            headerName: t("table.user.lastConnection"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.lastSessionDate
                            ? moment
                                .utc(data.lastSessionDate)
                                .local()
                                .format("LLL")
                            : t(`table.user.noSession`)}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.lastSessionDate
                        ? moment.utc(data.lastSessionDate).local().format("LLL")
                        : t(`table.user.noSession`);
                },
            },
        },
    ];
    return columns;
};

interface IUseRawScansColumnsProps {
    hasUserName: boolean;
}

export const useRawScansColumns = ({ hasUserName: hasUserNameColumn }: IUseRawScansColumnsProps) => {
    const { t } = useTranslation();

    const columns: IColumn<IScan>[] = [
        {
            field: "date",
            headerName: t(`table.scan.date`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {moment.utc(data.date).local().format("LLL:ss")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return moment.utc(data.date).local().format("LLL:ss");
                },
            },
        },
        {
            field: "buildingName",
            headerName: t("table.scan.building"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.isInOut ? NO_DATA : data.buildingName || NO_DATA}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.isInOut ? NO_DATA : data.buildingName || NO_DATA;
                },
            },
        },
        {
            field: "floorName",
            headerName: t("table.scan.floor"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.floorName || NO_DATA}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.floorName || NO_DATA;
                },
            },
        },
        {
            field: "localName",
            headerName: t(`table.scan.room`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.localName
                            ? data.localName
                            : data.isInOut
                                ? NO_DATA
                                : t("table.scan.nonExistentRoom")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    if (data.localName) {
                        return data.localName;
                    } else if (data.isInOut) {
                        return NO_DATA;
                    } else {
                        return t("table.scan.nonExistentRoom");
                    }
                },
            },
        },
        {
            field: "localCode",
            headerName: t(`table.scan.code`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.localCode}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.localCode;
                },
            },
        },
        {
            field: "measureName",
            headerName: t(`table.scan.type`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {t(`table.scan.${data.measureName}`)}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return t(`table.scan.${data.measureName}`);
                },
            },
        },
        {
            field: "version",
            headerName: t(`table.scan.appVersion`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.version}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.version;
                },
            },
        },
        {
            field: "selfieUrl",
            headerName: t("table.scan.picture"),
            transform: {
                render: (data) => (
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        '&:hover': {
                            outline: 'none'
                        }
                    }}>
                        {!data.selfieUrl ? (
                            <NoSelfieIcon />
                        ) : (
                            <Tooltip
                                title={
                                    <Box
                                        component="img"
                                        src={data.selfieUrl}
                                        sx={{
                                            maxWidth: 400,
                                            maxHeight: 400,
                                            objectFit: "contain",
                                            border: "none",
                                            boxShadow: "none",
                                            display: 'block',
                                        }}
                                        alt="Selfie preview"
                                    />
                                }
                                placement="right"
                                arrow
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            border: "none",
                                            boxShadow: "none",
                                            backgroundColor: 'transparent',
                                            padding: 0
                                        },
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={data.selfieUrl}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        objectFit: "cover",
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        border: "none",
                                        boxShadow: "none",
                                        '&:hover': {
                                            border: "none",
                                            boxShadow: "none"
                                        }
                                    }}
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = "/fallback-image.jpg";
                                        target.style.border = "none";
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                ),
                filterTransform: (data) => (!data.selfieUrl ? "X" : "O"),
            },
        }
    ];

    if (hasUserNameColumn) {
        columns.unshift({
            field: "username",
            headerName: t(`table.scan.employee`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.username || NO_DATA}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.username || NO_DATA;
                },
            },

        });
    }

    return columns;
};


export const useLocalScansColumns = () => {
    const { t } = useTranslation();

    const columns: IColumn<IScanDisplayWithId>[] = [
        {
            field: "username",
            headerName: t("table.scan.employee"),
        },
        {
            field: "date",
            headerName: t(`table.scan.startDate`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {moment.utc(data.date).local().format("LLL")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return moment.utc(data.date).local().format("LLL");
                },
            },
        },
        {
            field: "endDate",
            headerName: t(`table.scan.endDate`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {Number(data.minutes) >= 1
                            ? moment
                                .utc(data.date)
                                .local()
                                .add(Number(data.minutes), "minute")
                                .format("LLL")
                            : NO_DATA}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return Number(data.minutes) >= 1
                        ? moment
                            .utc(data.date)
                            .local()
                            .add(Number(data.minutes), "minute")
                            .format("LLL")
                        : NO_DATA
                },
            },
        },
        {
            field: "minutes",
            headerName: t(`table.scan.duration`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {Number(data.minutes) >= 1 ? Math.round(Number(data.minutes)) : 0} min
                    </Typography>
                ),
                filterTransform: (data) => {
                    return Number(data.minutes) >= 1 ? Math.round(Number(data.minutes)) : 0;
                },
            },
        },
    ];
    return columns;
};

interface IUseUserNotificationsColumnsProps {
    hasLocalName: boolean;
    hasUserName: boolean;
}

export const useUserNotificationsColumns = ({ hasLocalName, hasUserName }: IUseUserNotificationsColumnsProps) => {
    const { t } = useTranslation();

    const columns: IColumn<IUserNotificationDetail>[] = [
        {
            field: "type",
            headerName: t("table.userNotification.type.title"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {t(
                            `table.userNotification.type.${EUserNotificationCode[data.type]
                            }`
                        )}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return t(
                        `table.userNotification.type.${EUserNotificationCode[data.type]
                        }`
                    );
                },
            },
        },
        {
            field: "date",
            headerName: t(`table.userNotification.date`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {moment.utc(data.date).local().format("LLL")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return moment.utc(data.date).local().format("LLL");
                },
            },
        },
    ];

    if (hasLocalName) {
        columns.push({
            field: "localName",
            headerName: t(`table.userNotification.local`),
        },);
    }

    if (hasUserName) {
        columns.push({
            field: "username",
            headerName: t(`table.userNotification.user`),
        },);
    }

    return columns;
};

export const useTicketColumns = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    const columns: IColumn<ITicketDisplay>[] = [
        {
            field: "number",
            headerName: t(`table.employeeFeedback.ticketNumber`),
        },
        {
            field: "date",
            headerName: t(`table.employeeFeedback.date`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {moment.utc(data.date).local().format("LLL")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return moment.utc(data.date).local().format("LLL");
                },
            },
        },
        {
            field: "buildingName",
            headerName: t(`header.building`),
        },
        {
            field: "localName",
            headerName: t(`header.room`),
        },
        {
            field: "floorName",
            headerName: t(`header.floor`),
        },
        {
            field: "userName",
            headerName: t(`table.employeeFeedback.employee`),
        },
        {
            field: "request",
            headerName: t(`table.employeeFeedback.comment`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.request ? data.request.length > TEXT_MAX_DISPLAYED_LENGTH ?
                            data.request.substring(0, TEXT_MAX_DISPLAYED_LENGTH).concat("...") : data.request : "☓"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.request ? data.request.length > TEXT_MAX_DISPLAYED_LENGTH ?
                        data.request.substring(0, TEXT_MAX_DISPLAYED_LENGTH).concat("...") : data.request : "☓";
                },
            },
        },
        {
            field: "imageKeys",
            headerName: t(`table.employeeFeedback.images`),
            transform: {
                render: (data) => (
                    data.imageKeys ? <CameraAltIcon sx={{ color: theme.palette.primary.main }}></CameraAltIcon> : <NoPhotographyIcon></NoPhotographyIcon>
                ),
                filterTransform: (data) => {
                    return data.imageKeys ? "✓" : "☓";
                },
            },
        }
    ];
    return columns;
};

export const useFormColumns = () => {
    const { t } = useTranslation();

    const columns: IColumn<IForm>[] = [
        {
            field: "name",
            headerName: t(`form:formName`),
        },
        {
            field: "formType",
            headerName: t(`form:formType`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {t(`form:type.${EFormType[data.formType]}`)}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return t(`form:type.${EFormType[data.formType]}`);
                },
            },
        },
        {
            field: "propertyGroup",
            headerName: t(`form:propertyGroup`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.propertyGroup ? data.propertyGroup.name : "☓"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.propertyGroup ? data.propertyGroup.name : "☓";
                },
            },
        },
        {
            field: "building",
            headerName: t(`form:building`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.building ? data.building.name : "☓"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.building ? data.building.name : "☓";
                },
            },
        },
        {
            field: "floor",
            headerName: t(`form:floor`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.floor ? data.floor.name : "☓"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.floor ? data.floor.name : "☓";
                },
            },
        },
        {
            field: "local",
            headerName: t(`form:local`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.local ? data.local.name : "☓"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.local ? data.local.name : "☓";
                },
            },
        },
        {
            field: "user",
            headerName: t(`form:user`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.user ? data.user.name : "☓"}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.user ? data.user.name : "☓";
                },
            },
        },
        {
            field: "date",
            headerName: t(`form:formDate`),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {moment.utc(data.date).local().format("LLL")}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return moment.utc(data.date).local().format("LLL");
                },
            },
        },
        {
            field: "lang",
            headerName: t('form:language'),
            transform: {
                render: (data) => {
                    const lang = languages.get(data.lang);

                    return lang ? <img src={flags.get(lang)} height="15em" /> : ""
                }
                ,
                filterTransform: (data) => {
                    const lang = languages.get(data.lang);

                    return lang || "";
                }
            },
        },

    ];
    return columns;
};

interface IUseMiscleanedLocalsColumnsProps {
    hasTimeColumns: boolean;
}

export const useMiscleanedLocalsColumns = ({ hasTimeColumns }: IUseMiscleanedLocalsColumnsProps) => {
    const { t } = useTranslation();

    const columns: IColumn<IMiscleanedLocalsDisplay>[] = [
        {
            field: "date",
            headerName: t("header.date"),
        },
        {
            field: "building",
            headerName: t("header.building"),
        },
        {
            field: "floor",
            headerName: t("table.local.floor"),
        },
        {
            field: "name",
            headerName: t("local.name"),
        }
    ];

    if (hasTimeColumns) {
        columns.push(
            {
                field: "requiredTime",
                headerName: t("globalDashboard.requiredTime"),
                transform: {
                    render: (data) => (
                        <Typography variant="body2">
                            {data.requiredTime ? data.requiredTime : NO_DATA} {t("globalDashboard.minutes")}
                        </Typography>
                    ),
                    filterTransform: (data) => {
                        return data.requiredTime ? data.requiredTime : NO_DATA;
                    },
                },
            },
            {
                field: "actualTime",
                headerName: t("globalDashboard.actualTime"),
                transform: {
                    render: (data) => (
                        <Typography variant="body2">
                            {data.actualTime ? data.actualTime : NO_DATA} {t("globalDashboard.minutes")}
                        </Typography>
                    ),
                    filterTransform: (data) => {
                        return data.actualTime ? data.actualTime : NO_DATA;
                    },
                },
            }
        );
    }

    return columns;
};

export const useTimesheetOverviewColumns = () => {
    const { t } = useTranslation();
    const userRole = auth.user?.role;
    const isLimitedClient = userRole === ERole.LimitedClient;

    const columns: IColumn<IEmployeeTimesheetOverview>[] = [
        { field: "username", headerName: t("header.employee") },
        {
            field: "globalHours",
            headerName: t("globalDashboard.globalTime"),
            transform: {
                render: (data) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            {data.globalHours} {t("globalDashboard.hours")}
                        </Typography>
                        <PercentageChange value={data.globalPercentageChange} isPositiveValueBeneficial={true} />
                    </Box>
                ),
                filterTransform: (data) => {
                    return data.globalHours;
                },
            },
        },
        {
            field: "workedHours",
            headerName: t("globalDashboard.workedTime"),
            transform: {
                render: (data) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            {data.workedHours} {t("globalDashboard.hours")}
                        </Typography>
                        <PercentageChange value={data.workedPercentageChange} isPositiveValueBeneficial={true} />
                    </Box>
                ),
                filterTransform: (data) => {
                    return data.workedHours;
                },
            },
        },

    ]

    if (!isLimitedClient) {
        columns.push({
            field: "productivity",
            headerName: t("globalDashboard.productivity"),
            transform: {
                render: (data) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            {data.productivity !== undefined ? `${data.productivity}%` : "---"}
                        </Typography>
                        <PercentageChange value={data.productivityPercentageChange} isPositiveValueBeneficial={true} />
                    </Box>
                ),
                filterTransform: (data) => {
                    return data.productivity;
                },
            },
        },);
    }
    return columns;
}


export const useContactColumns = () => {
    const { t, i18n } = useTranslation();

    const columns: IColumn<IContact>[] = [
        {
            field: "name",
            headerName: t("table.contact.name"),
        },
        {
            field: "positionFrench",
            headerName: t("table.contact.position"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {eLanguages.get(i18n.language) === ELanguage.French ? data.positionFrench : data.positionEnglish}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return eLanguages.get(i18n.language) === ELanguage.French ? data.positionFrench : data.positionEnglish;
                },
            },
        },
        {
            field: "email",
            headerName: t("table.contact.email"),
        },
        {
            field: "phone",
            headerName: t("table.contact.phone"),
        },
    ];

    return columns;
}